import { FC, Fragment, ReactNode } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

/**
 * A drawer's body: a thin scrollbar (the page's own, in the text colour at a quarter strength, none at all
 * where the browser lets it overlay) and the first and last lines fading out at the edges, so a cut-off line
 * reads as "more above or below" and not as a hard crop. The padding keeps the text clear of the fades at rest.
 */
const DRAWER_SCROLL =
  "lg:pb-8 lg:pt-4 lg:[scrollbar-width:thin] lg:[scrollbar-color:rgba(128,128,128,0.4)_transparent] " +
  "lg:[&::-webkit-scrollbar]:w-1.5 lg:[&::-webkit-scrollbar-track]:bg-transparent " +
  "lg:[&::-webkit-scrollbar-thumb]:rounded-full lg:[&::-webkit-scrollbar-thumb]:bg-text-color/25 " +
  "lg:[-webkit-mask-image:linear-gradient(to_bottom,transparent,#000_16px,#000_calc(100%_-_24px),transparent)] " +
  "lg:[mask-image:linear-gradient(to_bottom,transparent,#000_16px,#000_calc(100%_-_24px),transparent)]";

/**
 * A sheet that slides up from the bottom of a phone (1NOW-11155): the price
 * details, the booking summary and the pay-later policies. From 1024px up it is
 * a centered modal instead (1NOW-11173): the pay-later policies and the price
 * details. `drawer` (1NOW-11174, the desktop steps' "View coverage", "Details" and
 * policy reader) makes it a right-side drawer from 1024px up and leaves it the
 * same bottom sheet below. Tenant tokens only
 * (bg-bg-color, text-text-color, secondary-100), so each client's branding
 * carries over. `z-[48]` keeps it above the full-screen booking sheet (z-45) and
 * below the Bonzah terms popup (z-50), which both can raise over it.
 */
const BottomSheet: FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Stays at the bottom of the sheet while the body scrolls. */
  footer?: ReactNode;
  /** Fills most of the screen instead of fitting its content. */
  tall?: boolean;
  /** From 1024px up: a drawer on the right, full height, with a thin scrollbar and faded edges. */
  drawer?: boolean;
}> = ({ open, onClose, title, children, footer, tall, drawer }) => (
  <Transition show={open} as={Fragment}>
    <Dialog as="div" className="relative z-[48]" onClose={onClose}>
      <TransitionChild
        as={Fragment}
        enter="ease-out duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-black/45" aria-hidden="true" />
      </TransitionChild>
      <div
        className={`fixed inset-x-0 bottom-0 ${
          drawer ? "lg:inset-y-0 lg:left-auto lg:right-0" : "lg:inset-0 lg:flex lg:items-center lg:justify-center lg:p-6"
        }`}
      >
        <TransitionChild
          as={Fragment}
          enter="transform transition ease-out duration-300"
          enterFrom={drawer ? "translate-y-full lg:translate-y-0 lg:translate-x-full" : "translate-y-full lg:translate-y-4 lg:opacity-0"}
          enterTo={drawer ? "translate-y-0 lg:translate-x-0" : "translate-y-0 lg:opacity-100"}
          leave="transform transition ease-in duration-200"
          leaveFrom={drawer ? "translate-y-0 lg:translate-x-0" : "translate-y-0 lg:opacity-100"}
          leaveTo={drawer ? "translate-y-full lg:translate-y-0 lg:translate-x-full" : "translate-y-full lg:translate-y-4 lg:opacity-0"}
        >
          <DialogPanel
            className={`flex w-full flex-col rounded-t-3xl border border-b-0 border-secondary-100 bg-bg-color text-text-color ${
              drawer
                ? `lg:h-screen lg:max-h-none lg:w-[520px] lg:max-w-[100vw] lg:rounded-none lg:border-b-0 lg:border-r-0 lg:shadow-xl ${
                    tall ? "h-[92vh]" : "max-h-[88vh]"
                  }`
                : `lg:max-w-2xl lg:rounded-3xl lg:border-b ${
                    tall ? "h-[92vh] lg:h-[85vh]" : "max-h-[88vh] lg:max-h-[85vh]"
                  }`
            }`}
          >
            <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-secondary-100 lg:hidden" aria-hidden="true" />
            <div
              className={`flex shrink-0 items-center justify-between px-4 pb-1 pt-3 lg:px-6 lg:pt-4 ${
                drawer ? "lg:border-b lg:border-secondary-100 lg:pb-3 lg:pt-4" : ""
              }`}
            >
              <DialogTitle className={`text-lg font-extrabold text-text-color ${drawer ? "lg:font-semibold" : ""}`}>{title}</DialogTitle>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full text-text-color"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div
              className={`min-h-0 flex-1 overflow-y-auto px-4 pb-4 lg:px-6 ${drawer ? DRAWER_SCROLL : ""}`}
            >
              {children}
            </div>
            {footer && (
              <div className="shrink-0 border-t border-secondary-100 px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 lg:px-6">
                {footer}
              </div>
            )}
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </Transition>
);

export default BottomSheet;
